import {
  CoinType,
  Inventory,
  InventoryCategory,
  Query,
} from "@onmo/graphql/graphql";
import {
  getListInventoriesGQL,
  getUserInventoriesGQL,
  meGQL,
} from "../../graphql";
import { useQuery } from "@apollo/client";
import { InventoryStatus } from "@onmo/graphql/graphql";
import { useMemo } from "react";

const REVIVE_INVENTORY_ID = import.meta.env.VITE_REVIVE_INVENTORY_ID;

const inputListBundle = {
  input: {
    filters: {
      restrictToBundles: true,
      restrictToFree: false,
      status: [InventoryStatus.Available, InventoryStatus.Restricted],
    },
  },
};

const inputListAvatar = {
  input: {
    filters: {
      restrictToItems: true,
      categories: [InventoryCategory.Avatar],
      status: [InventoryStatus.Available, InventoryStatus.Restricted],
    },
  },
};

const inputReviveInventory = {
  input: {
    filters: { status: [InventoryStatus.Hidden] },
  },
};

const LIMIT = 50;

export const useFetchReviveInventory = () => {
  const { data } = useQuery<Query>(getListInventoriesGQL, {
    variables: inputReviveInventory,
  });
  const reviveInventory = data?.inventories.items;
  const reviveInventoryAvailable = reviveInventory?.find(
    (item: Inventory) => item.id === REVIVE_INVENTORY_ID,
  );
  return { reviveInventory, reviveInventoryAvailable };
};

//fetch user inventories
export const useFetchOwnedInventory = () => {
  const { data, fetchMore } = useQuery(getUserInventoriesGQL, {
    variables: {
      input: {
        page: { limit: LIMIT, offset: 0 },
        filters: {
          status: [InventoryStatus.Available, InventoryStatus.Restricted],
        },
      },
    },
  });
  const ownedInventory = data?.me?.inventories?.items;

  const loadMore = () => {
    if (
      !data ||
      data?.me?.inventories?.total === data?.me?.inventories?.items?.length
    )
      return;
    fetchMore({
      variables: {
        input: {
          page: { limit: LIMIT, offset: ownedInventory?.length },
        },
      },
    });
  };

  return {
    ownedInventory: ownedInventory?.filter((item: Inventory) => !item.isBundle),
    loadMore,
  };
};

//fetch bundles inventories
export const useFetchBundlesInventory = () => {
  const { data, fetchMore } = useQuery(getListInventoriesGQL, {
    variables: inputListBundle,
  });
  const bundlesInventory = data?.inventories?.items;
  const bundlesInventoryAvailable = data?.inventories?.items?.filter(
    (item: Inventory) => !item.isPurchased,
  );
  const bundlesInventoryOwned = data?.inventories?.items?.filter(
    (item: Inventory) => item.isPurchased,
  );

  const loadMore = () => {
    if (!data) return;
    fetchMore({
      variables: {
        input: {
          page: { limit: LIMIT, offset: bundlesInventory?.length },
        },
      },
    });
  };
  return {
    bundlesInventory,
    bundlesInventoryOwned,
    bundlesInventoryAvailable,
    loadMore,
  };
};

//fetch avatars inventories
export const useFetchAvatarsInventory = () => {
  const { data, fetchMore } = useQuery(getListInventoriesGQL, {
    variables: inputListAvatar,
  });
  const avatarsInventory = data?.inventories?.items;

  const loadMore = () => {
    if (!data) return;
    fetchMore({
      variables: {
        input: {
          page: { limit: LIMIT, offset: avatarsInventory.length },
        },
      },
    });
  };

  return { avatarsInventory, loadMore };
};
//fetch avatars by bundle
export const useFetchAvatarsByBundle = (bundleId: string) => {
  const { data } = useQuery(getListInventoriesGQL, {
    variables: {
      input: {
        filters: {
          bundleInventoryId: bundleId,
          status: [InventoryStatus.Available, InventoryStatus.Restricted],
        },
      },
    },
  });
  const avatarsByBundle = data?.inventories?.items;
  const avatarsByBundleAvailable = data?.inventories?.items?.filter(
    (item: Inventory) => !item.isPurchased,
  );

  return { avatarsByBundle, avatarsByBundleAvailable };
};
export const useFetchCoinWallet = () => {
  const { data: dataMe } = useQuery<Query>(meGQL);

  const spendableGems = useMemo(() => {
    return (
      dataMe?.me?.coinWallets?.find(
        (wallet) => wallet.itemType === CoinType.SpendableGems,
      )?.balance || 0
    );
  }, [dataMe]);

  return { spendableGems };
};
